import { useContext, useEffect, useReducer, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useHouseholdQuery, useMessagingSequenceQuery, useWeddingWebsiteQuery } from '../../../generated/graphql';
import { TrackingContext } from '../../../TrackingContext';
import { SeqIdParam } from '../../SharePage/helper';

interface State {
  showSuccess: boolean;
  showError: boolean;
  errorMessage: string | undefined;
  hideRecaptcha: boolean;
  guestIds: string[];
}

type Action =
  | { type: 'RESET'; payload: { hideRecaptcha: boolean; showSuccess: boolean; showError: boolean } }
  | { type: 'SUCCESS' }
  | { type: 'ERROR'; payload?: { message: string | undefined } }
  | { type: 'SET_GUESTIDS'; payload: { guestIds: string[] } }
  | { type: 'SET_CAPTCHA'; payload: { hideRecaptcha: boolean } };

function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'RESET':
    case 'SET_GUESTIDS':
    case 'SET_CAPTCHA':
      return { ...state, ...action.payload };
    case 'SUCCESS':
      return { ...state, showSuccess: true, guestIds: [] };
    case 'ERROR':
      return {
        ...state,
        showError: true,
        guestIds: [],
        errorMessage: action.payload?.message?.replace('GraphQL error:', ''),
      };
    default:
      throw Error('Not implemented');
  }
}

export const useSequenceState = () => {
  const [state, dispatch] = useReducer(reducer, {
    showSuccess: false,
    showError: false,
    errorMessage: undefined,
    hideRecaptcha: true,
    guestIds: [],
  });
  const { data: wwsData, loading: wwsLoading } = useWeddingWebsiteQuery();
  const { data: householdData, loading: householdLoading } = useHouseholdQuery();
  const { seqId } = useParams<SeqIdParam>();

  const { data: sequence } = useMessagingSequenceQuery({
    variables: { id: seqId },
  });

  const recaptchaRef = useRef<{ reset: () => void; getValue: () => string }>(null);
  const setHideRecaptcha = (val: boolean) => dispatch({ type: 'SET_CAPTCHA', payload: { hideRecaptcha: val } });
  const setGuestIds = (ids: string[]) => dispatch({ type: 'SET_GUESTIDS', payload: { guestIds: ids } });

  const { setPagesLoaded } = useContext(TrackingContext);

  useEffect(() => {
    setPagesLoaded(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultMessage = sequence?.messagingSequence?.defaultSmsBody ?? '';

  const greeting = `Sent via The Knot: Hey [guest name], it's ${wwsData?.member?.firstName} & ${wwsData?.member?.fianceFirstName}!`;
  const optout = `To unsubscribe from ${wwsData?.member?.firstName} & ${wwsData?.member?.fianceFirstName} reply NO. Msg & data rates apply. To unsubscribe from all couples using The Knot reply STOP.`;

  return {
    ...state,
    dispatch,
    recaptchaRef,
    setHideRecaptcha,
    setGuestIds,
    wwsData,
    wwsLoading,
    sequence,
    seqId,
    householdData,
    householdLoading,
    greeting,
    optout,
    defaultMessage,
  };
};
